import clsx from "clsx";
import React, { ForwardedRef } from "react";
import ReactSelect, { CSSObjectWithLabel, StylesConfig } from "react-select";
import CreatableSelect from "react-select/creatable";
import styled from "styled-components";

import { FormErrorModel, IStyledAttributes } from "@ryde/shared";
import { greyMui, rydeColors } from "@ryde/theme";

import { ReactSelectCommonProps } from "./dropdowns.models";

type IReactSelect = ReactSelectCommonProps<unknown, boolean> & {
  id?: string;
  width?: string;
  error?: FormErrorModel;
};

interface IReactSelectState {
  isSelected?: boolean;
  isFocused?: boolean;
  isDisabled?: boolean;
  data?: unknown;
}

const SelectStyled = styled(ReactSelect).attrs<IStyledAttributes>({
  "data-e2e": "select-ordinary",
})<IReactSelect>`
  width: ${props => props.width || "100%"};

  & .Select-placeholder {
    font-size: smaller;
  }

  &.error .Select--multi__control {
    border-color: #fe0000;
  }
`;

const MultiSelectStyled = styled(ReactSelect).attrs<IStyledAttributes>({
  "data-e2e": "multi-select-ordinary",
})<IReactSelect>`
  width: ${props => props.width || "100%"};

  &.Select--multi {
    .Select-value {
      display: inline-flex;
      align-items: center;
    }
  }
  & .Select-placeholder {
    font-size: smaller;
  }

  &.error .Select--multi__control {
    border-color: #fe0000;
  }
`;

const CreatableMultiSelectStyled = styled(
  CreatableSelect,
).attrs<IStyledAttributes>({
  "data-e2e": "creatable-multi-select",
})<IReactSelect>`
  width: ${props => props.width || "100%"};

  &.Select--multi {
    .Select-value {
      display: inline-flex;
      align-items: center;
    }
  }
  & .Select-placeholder {
    font-size: smaller;
  }

  &.error .Select--multi__control {
    border-color: #fe0000;
  }
`;

const SelectErrorStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "select-ordinary-error",
})`
  color: #fe0000;
  margin-inline-start: 6px;
`;

const customSelectStyles: StylesConfig = {
  control: (provided: CSSObjectWithLabel, state: IReactSelectState) =>
    ({
      ...provided,
      borderRadius: "8px",
      minHeight: "46px",
      borderColor: state.isFocused
        ? rydeColors.focusedControl
        : provided.borderColor,
      outline: state.isFocused ? 0 : provided.outline,
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgb(13 110 253 / 25%)"
        : provided.boxShadow,
    }) as CSSObjectWithLabel,
  multiValue: (provided: CSSObjectWithLabel) =>
    ({
      ...provided,
      background: "#F3F3FE",
      border: "1px solid rgba(102, 89, 253, 0.5)",
      borderRadius: "8px",
      height: "32px",
      padding: "4px 8px",
    }) as CSSObjectWithLabel,
  multiValueLabel: (provided: CSSObjectWithLabel) =>
    ({
      ...provided,
      fontWeight: 400,
    }) as CSSObjectWithLabel,
  multiValueRemove: (provided: CSSObjectWithLabel) =>
    ({
      ...provided,
      padding: 0,
      color: "#707070",
      ":hover": {
        ...provided[":hover"],
      },
    }) as CSSObjectWithLabel,
  indicatorSeparator: () =>
    ({
      display: "none",
    }) as CSSObjectWithLabel,
};

const customMultiSelectStyles: StylesConfig = {
  dropdownIndicator: () => ({
    display: "none",
  }),
  control: (provided: CSSObjectWithLabel, state: IReactSelectState) =>
    ({
      ...provided,
      borderRadius: "8px",
      minHeight: "46px",
      borderColor: state.isFocused
        ? rydeColors.focusedControl
        : provided.borderColor,
      outline: state.isFocused ? 0 : provided.outline,
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgb(13 110 253 / 25%)"
        : provided.boxShadow,
      ":before": {
        content: '"+"',
        marginInlineStart: 8,
      },
    }) as CSSObjectWithLabel,
  option: (provided: CSSObjectWithLabel, state: IReactSelectState) =>
    ({
      ...provided,
      fontWeight: 400,
      color: greyMui._700,
      backgroundColor: state.isFocused
        ? rydeColors.focusedOption
        : rydeColors.white,
      ":hover": {
        ...provided[":hover"],
      },
      ":before": {
        content: '" "',
        display: "inline-block",
        width: "18px",
        height: "18px",
        borderRadius: "3px",
        margin: "0 20px -4px 20px",
        backgroundColor: state.isSelected
          ? rydeColors.lightGreen
          : rydeColors.greyBorderButton,
      },
    }) as CSSObjectWithLabel,
};

export const Select = (props: IReactSelect): JSX.Element => {
  const customizedProps = { ...props, isSearchable: true };
  return (
    <>
      <SelectStyled
        classNamePrefix={"Select--multi"}
        data-e2e={`select_classic`}
        isClearable={props.isClearable}
        styles={customSelectStyles}
        {...customizedProps}
        className={clsx({ error: props.error }, props.className)}
      />
      {!!props.error && (
        <SelectErrorStyled>{props.error as string}</SelectErrorStyled>
      )}
    </>
  );
};

export const MultiSelect = React.forwardRef(
  (props: IReactSelect, ref: ForwardedRef<never>) => {
    const customizedProps = {
      ...props,
      isSearchable: true,
    };
    return (
      <>
        <MultiSelectStyled
          classNamePrefix={"Select--multi"}
          data-e2e={`multi_select_classic`}
          isMulti
          isClearable={props.isClearable}
          hideSelectedOptions={props.hideSelectedOptions}
          ref={ref}
          styles={{ ...customSelectStyles, ...customMultiSelectStyles }}
          {...customizedProps}
          className={clsx({ error: props.error }, props.className)}
        />
        {!!props.error && (
          <SelectErrorStyled>{props.error as string}</SelectErrorStyled>
        )}
      </>
    );
  },
);

export const CreatableMultiSelect = React.forwardRef(
  (props: IReactSelect, ref: ForwardedRef<never>) => {
    const customizedProps = {
      ...props,
      isSearchable: true,
    };
    return (
      <>
        <CreatableMultiSelectStyled
          classNamePrefix={"Select--multi"}
          data-e2e={`creatable_multi_select_classic`}
          isMulti
          isClearable={props.isClearable}
          hideSelectedOptions={props.hideSelectedOptions}
          ref={ref}
          styles={{ ...customSelectStyles, ...customMultiSelectStyles }}
          {...customizedProps}
          className={clsx({ error: props.error }, props.className)}
        />
        {!!props.error && (
          <SelectErrorStyled>{props.error as string}</SelectErrorStyled>
        )}
      </>
    );
  },
);
